import { Modal } from 'antd';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import styles from './export-modal.module.css';
import LazyModal, { LazyModalComponentProps } from '../../../modules/ui/components/lazy-modal';
import { closeModal } from '../../../modules/ui/store/ui-slice';
import { exportTraceLogsModalId } from '../../constants/constants';
import { CloseIcon } from '../../icons/close-icon';
import CustomButton from '../buttons/custom-button/custom-button';
import IconWrapper from '../icon-wrapper/icon-wrapper';
import ModalConfirmationContent from '../modal-confirmation-content/modal-confirmation-content';
import { ExportType } from './export-type';

interface Props {
  type?: ExportType;
  customParams?: string;
}

const ExportModalContent = ({
  isOpen,
  afterClose,
  type,
  customParams,
}: LazyModalComponentProps & Props): JSX.Element => {
  const { t } = useTranslation('common');
  const dispatch = useDispatch();
  const closeExportModal = () => dispatch(closeModal(exportTraceLogsModalId));
  const params = useParams();

  const exportLink = useMemo(() => {
    if (type === ExportType.Dashboard) {
      return `${process.env.REACT_APP_API_URL}/portal-api/v1/dashboard/export?${customParams}`;
    }

    if (!params.id) {
      return `${process.env.REACT_APP_API_URL}/portal-api/v1/${type}/trace-logs/export`;
    }

    if (type && params.id) {
      const formattedParams = new URLSearchParams({ [type]: params.id }).toString();
      return `${process.env.REACT_APP_API_URL}/portal-api/v1/${type}/${params.id}/trace-logs/export?${formattedParams}`;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.id, customParams, type]);

  return (
    <Modal
      className={styles.modal}
      title={t('label.exportModal.title')}
      open={isOpen}
      onCancel={closeExportModal}
      closeIcon={
        <IconWrapper>
          <CloseIcon />
        </IconWrapper>
      }
      afterClose={afterClose}
      footer={[
        <CustomButton type="secondary" key="back" onClick={closeExportModal} className={styles.exportCancel}>
          {t('action.cancel')}
        </CustomButton>,
        <a key="export" href={exportLink} download>
          <CustomButton type="primary" onClick={closeExportModal}>
            {t('action.export')}
          </CustomButton>
        </a>,
      ]}
    >
      <ModalConfirmationContent confirmDeleteText={t('label.exportModal.warning')} />
    </Modal>
  );
};

const ExportModal = (): JSX.Element => {
  return <LazyModal modalId={exportTraceLogsModalId} Component={ExportModalContent} />;
};

export default ExportModal;
